import { useCallback } from 'react';
import { analyticsAPI } from '../../../services/analytics';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const STATUS_LABELS = ['Delivered', 'Running', 'Cancelled'];
const STATUS_VALUES = ['Delivered', 'running_order', 'Cancelled'];
const PAYMENT_MODES = ['UPI', 'COD'];

const openModal = (setter, data) => setTimeout(() => setter(data), 300);

export const useChartHandlers = (filters, dashboardData, setOrderModal, setUserModal) => {
  const handleMonthlyChartClick = useCallback(async (event, elements) => {
    if (!elements.length) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    try {
      let response, monthLabel;
      if (filters.startDate && filters.endDate) {
        response = await analyticsAPI.getOrdersByDateRange(filters.startDate, filters.endDate);
        monthLabel = `${filters.startDate} to ${filters.endDate}`;
      } else {
        const monthIndex = elements[0].index + 1;
        response = await analyticsAPI.getOrdersByMonth(filters.year, monthIndex);
        monthLabel = MONTH_NAMES[monthIndex - 1];
      }
      openModal(setOrderModal, { isOpen: true, orders: response.data.orders || [], month: monthLabel, year: filters.year });
    } catch {}
  }, [filters, setOrderModal]);

  const handleUserChartClick = useCallback((event, elements) => {
    if (!elements.length) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const { datasetIndex, index } = elements[0];

    if (filters.startDate && filters.endDate && dashboardData.dayWiseUserData) {
      const dayData = dashboardData.dayWiseUserData[index];
      if (dayData) openModal(setUserModal, { isOpen: true, users: dayData.users, month: dayData.day, year: filters.year });
      return;
    }

    const isCurrentYear = datasetIndex === 0;
    const year = isCurrentYear ? filters.year : filters.compareYear;
    const monthlyData = isCurrentYear ? dashboardData.userMonthlyData : dashboardData.compareUserMonthlyData;
    if (monthlyData?.[index]?.users) {
      openModal(setUserModal, { isOpen: true, users: monthlyData[index].users, month: MONTH_NAMES[index], year });
    }
  }, [filters, dashboardData.userMonthlyData, dashboardData.compareUserMonthlyData, dashboardData.dayWiseUserData, setUserModal]);

  const handleStatusChartClick = useCallback(async (event, elements) => {
    if (!elements.length) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const index = elements[0].index;
    try {
      const response = filters.startDate && filters.endDate
        ? await analyticsAPI.getOrdersByDateRange(filters.startDate, filters.endDate, STATUS_VALUES[index])
        : await analyticsAPI.getOrdersByStatus(STATUS_VALUES[index], filters.year);
      openModal(setOrderModal, { isOpen: true, orders: response.data.orders || [], month: STATUS_LABELS[index], year: filters.year });
    } catch {}
  }, [filters, setOrderModal]);

  const handlePaymentChartClick = useCallback(async (event, elements) => {
    if (!elements.length) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const paymentMode = PAYMENT_MODES[elements[0].index];
    try {
      const response = filters.startDate && filters.endDate
        ? await analyticsAPI.getOrdersByDateRange(filters.startDate, filters.endDate, null, paymentMode)
        : await analyticsAPI.getOrdersByPaymentMode(paymentMode, filters.year);
      openModal(setOrderModal, { isOpen: true, orders: response.data.orders || [], month: paymentMode, year: filters.year });
    } catch {}
  }, [filters, setOrderModal]);

  const handleCityChartClick = useCallback((event, elements) => {
    if (!elements.length) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const cityName = dashboardData.cityChart?.labels[elements[0].index];
    const cityOrders = dashboardData.cityOrdersData?.[cityName]?.orders || [];
    openModal(setOrderModal, { isOpen: true, orders: cityOrders, month: cityName, year: filters.year });
  }, [dashboardData.cityChart, dashboardData.cityOrdersData, filters.year, setOrderModal]);

  const handleCityUsersChartClick = useCallback((event, elements) => {
    if (!elements.length) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const cityName = dashboardData.cityUsersChart?.labels[elements[0].index];
    const cityUsers = Object.values(dashboardData.cityUsersRawData?.[cityName] || {});
    openModal(setUserModal, { isOpen: true, users: cityUsers, month: cityName, year: filters.year });
  }, [dashboardData.cityUsersChart, dashboardData.cityUsersRawData, filters.year, setUserModal]);

  return {
    handleMonthlyChartClick,
    handleUserChartClick,
    handleStatusChartClick,
    handlePaymentChartClick,
    handleCityChartClick,
    handleCityUsersChartClick
  };
};
